import Vue from 'vue';
import Store from "../../data/store";
import * as showdown from 'showdown';
import { daysAgoISO, daysFromNowISO, formatDateTimeISO, startOfDay, endOfDay } from '../../util/dateUtils';
import Datepicker from "vuejs-datepicker";

export const ApckNewsAndUpdates = {
    components: {
        'datepicker': Datepicker,
    },
    props: {
        src: {
            type: String,
            required: true,
            default: '/news'
        },
        start: {
            type: Number,
            default: 0
        },
        limit: {
            type: Number,
            default: 30
        },
        sort: {
            type: String,
            default: null
        },
        dir: {
            type: String,
            default: null
        },
        method: {
            type: String,
            default: function () {
                return 'post';
            }
        }
    },
    data: function() {
        return {
            rows:  [],
            store: null,
            loaded: false,
            showScrollMask: false,
            selectedTagFilter: null,
            filters: [
                {name: 'release', icon: 'fa-tag', label: 'Release'},
                {name: 'vulnerability', icon: 'fa-bug', label: 'Vulnerability'},
                {name: 'news', icon: 'fa-newspaper', label: 'News'},
                {name: 'blog', icon: 'fa-message-pen', label: 'Blog'},
            ],
            showDatePicker: false,
            datePickerOpen: false,
            start_date: daysAgoISO(7),
            end_date: daysFromNowISO(1),
            _loadingMore: false,
        };
    },
    methods: {
        dataSourceIcon: function(source) {
            let type_icon = 'fa-bell';
            let map = {
                'news':'fa-newspaper',
                'blog': 'fa-message-pen',
                'release': 'fa-tag',
                'vulnerability': 'fa-bug',
                'scanner plugin': 'fa-bug',
                'ui': 'fa-desktop'
            };
            if (map[source]) {
                type_icon = map[source];
            }
            return type_icon;
        },
        tags: function(row) {
            let _tags = row.source;
            if (row.source === 'gitlab'){
                _tags = 'release';
            }
            return _tags;
        },
        markdown: function(text) {
            var converter = new showdown.Converter();
            if (text) {
                return converter.makeHtml(text);
            }
        },
        selectFilter(filter) {
            if (this.selectedTagFilter === filter.name) {
                this.selectedTagFilter = null;
            } else {
                this.selectedTagFilter = filter.name;
            }
        },
        formatDate: function (date) {
            var cmp = this;
            if (date === null) {
                return '';
            }
            try {
                var tzoffset = date.getTimezoneOffset() * 60000;
                return (new Date(date.getTime() - tzoffset)).toISOString().slice(0, 10);
            } catch (e) {
                cmp.$flash({
                    type: 'error',
                    message: "Please enter a valid date in the format DD/MM/YYYY"
                });
                return '';
            }
        },
        setStartDate: function(value) {
            const date = this.formatDate(value);
            if (date === '') {
                this.start_date = daysAgoISO(7);
            } else {
                this.start_date = date
            }
            this.setParams();
        },
        setEndDate: function(value) {
            const date = this.formatDate(value)
            if (date === '') {
                this.end_date = daysFromNowISO(1);
            } else {
                this.end_date = date;
            }
            this.setParams();
        },
        setParams: function() {
            let start_date = formatDateTimeISO(startOfDay(new Date(this.start_date)));
            let end_date = formatDateTimeISO(endOfDay(new Date(this.end_date)));
            this.store.options['start_date'] = start_date;
            this.store.options['end_date'] = end_date;
            this.store.options.start = 0;  // reset the "load more" progress when we change date
            this.store.load(this.store.options, undefined, false, true);
        },
        loadMore() {
            this.$data._loadingMore = true;
            this.store.options.start = this.rows.length;
            this.store.load(this.store.options, undefined, false, true);
        },
        checkScroll() {
            const container = this.$refs.scrollContainer;
            this.showScrollMask = container.scrollHeight - container.scrollTop > container.clientHeight + 5;
        },
    },
    computed: {
        visibleRows() {
            if (this.selectedTagFilter === null) {
                return this.rows;
            } else {
                return this.rows.filter(item => item.data.source === this.selectedTagFilter);
            }
        },
        canLoadMore() {
            return this.store && this.store.total > this.store.results;
        }
    },
    mounted: function() {
        var cmp = this;
        // Mount a data store on creation
        var store = new Store({
            page: 1,
            url: cmp.src,
            method: cmp.method,
            autoLoad: false,
            start: cmp.start,
            limit: cmp.limit,
            sort: cmp.sort,
            dir: cmp.dir,
        });

        store.on('beforeload', function() {
           cmp.loaded = false;
        });

        store.on('datachanged', function(store, rawdata) {
            cmp.loaded = true;
            if (!cmp.$data._loadingMore) {
                cmp.rows = store.data;
            } else {
                cmp.rows = [...cmp.rows, ...store.data];
                cmp.store.results = cmp.rows.length;
                cmp.$data._loadingMore = false;
            }
            cmp.$nextTick(() => {
                cmp.checkScroll();
            });
        });
        store.on('errorload', function (evt, store, rawdata) {
            cmp.loaded = true;
            if (!cmp.$data._loadingMore) {
                cmp.rows = [];
            } else {
                cmp.$data._loadingMore = false;
            }
            cmp.$nextTick(() => {
                cmp.checkScroll();
            });
        });

        cmp.store = store;
        this.setParams();
    },
    template: `
        <div class="dashboard-sidebar-panel">
            <div>
                <div class="header">
                    <h2>News</h2>
                    <div
                        :class="['badge apck-news-dates', datePickerOpen ? 'active' : '']"
                        @click="datePickerOpen = !datePickerOpen"
                    >
                        {{ start_date | formatDate }} - {{ end_date | formatDate }}
                    </div>
                </div>
                <div v-if="datePickerOpen" class="apck-news-date-dropdown">
                    <div class="form-field">
                        <label>Start Date</label>
                        <datepicker
                            @selected="event => {setStartDate(event)}"
                            :value="start_date"
                            format="dd/MM/yyyy"
                            placeholder="Start Date"
                            :disabled-dates="{'from': end_date ? new Date(end_date) : new Date()}"
                            clear-button
                            clear-button-icon="fa-regular fa-times"
                            calendar-button
                            calendar-button-icon="fa-regular fa-calendar"
                        />
                    </div>
                    <div class="form-field">
                        <label>End Date</label>
                        <datepicker
                            @selected="event => {setEndDate(event)}"
                            :value="end_date"
                            format="dd/MM/yyyy"
                            placeholder="End Date"
                            :disabled-dates="{'to': new Date(start_date), 'from': new Date()}"
                            clear-button
                            clear-button-icon="fa-regular fa-times"
                            calendar-button
                            calendar-button-icon="fa-regular fa-calendar"
                        />
                    </div>
                </div>
            </div>
            <div class="apck-news-filters">
                <div
                    v-for="filter in filters"
                    :class="['news-filter-' + filter.name, selectedTagFilter === filter.name ? 'selected': '']"
                    @click="selectFilter(filter)"
                >
                    <span v-if="selectedTagFilter === filter.name" :class="['fa-regular', filter.icon]"></span>
                    {{ filter.label }}
                </div>
            </div>
            <div class="apck-news" ref="scrollContainer" @scroll="checkScroll()">
                <template v-if="!visibleRows.length">
                    <p>
                        No results found
                    </p>
                </template>
                <template v-else>
                    <div v-for="row in visibleRows" v-bind:class="['apck-news-row', 'news-row-' + row.data.source]">
                        <div :class="['alert-news-icon', 'news-color-' + row.data.source]">
                            <i :class="['fa-regular', dataSourceIcon(row.data.source)]"></i>
                        </div>

                        <div class="alert-news-body">
                            <a
                                v-if="row.data.external_url"
                                v-bind:href="row.data.external_url"
                                target="_blank"
                                class="alert-news-title"
                            >
                                <h3>{{ row.data.title }}</h3>
                            </a>
                            <div v-else class="alert-news-title">
                                <h3>{{ row.data.title }}</h3>
                            </div>

                            <p class="apck-news-date">
                                {{ row.data.published | formatDate }}
                                •
                                {{ tags(row.data) | title }}
                            </p>

                            <div class="news-details" v-html="markdown(row.data.message)"/>
                        </div>
                    </div>

                    <div
                        v-if="canLoadMore"
                        @click="loadMore"
                        class="apck-center btn btn-primary"
                    >
                        {{ store.total - store.results }} remaining. Load {{ Math.min(limit, store.total - store.results) }} More...
                    </div>
                </template>
            </div>

            <div v-show="showScrollMask" class="scroll-mask"></div>

            <apck-mask :trigger="!loaded"/>
        </div>
    `
}